import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  Snackbar,
  Alert,
  Tab,
  Tabs,
  ButtonGroup,
} from "@mui/material";
import axios from "../../utils/axiosInstance";
import ManageRecipient from "./ManageRecipients";
import DeleteRecipient from "./DeleteRecipient";
import NotificationHistory from "./NotificationHistory";
import AppsIcon from "@mui/icons-material/Apps";
import { formatDate } from "../../utils/formatDate";

import GroupIcon from "@mui/icons-material/Group";
import CallIcon from "@mui/icons-material/Call";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";

import { useLocation, useNavigate } from "react-router-dom";
import PriorityVoice from "./PriorityVoice";
import SMSDelivery from "./SmsDelivery";
import { SmsOutlined } from "@mui/icons-material";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";
import EmailDelivery from "./EmailDelivery";

function buildUpdateKey(member, field) {
  const ids = Array.isArray(member.members)
    ? member.members.map((m) => m.id).join(",")
    : member.recipient?.id ?? member.id;

  return `${ids}-${field}`;
}

export default function MainAlarm({ device }) {
  const theme = useTheme();

  const [currentTab, setCurrentTab] = useState("add-recipient");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [updatingUsers, setUpdatingUsers] = useState(new Set());
  const [loadingUpdate, setLoadingUpdate] = useState(false);

  const truncateText = (text, maxLength = 45) => {
    if (!text) return "";
    return text.length > maxLength
      ? "..." + text.substring(text.length - maxLength)
      : text;
  };

  useEffect(() => {
    // Add null check for device
    if (device && device.id) {
      fetchLinked();
    } else {
      setLoading(false); // Stop loading if device is not available
    }
  }, [device?.id]); // Only depend on device.id to prevent unnecessary re-renders

  // Memoize filtered users for the table display only
  const filteredUsersForTable = React.useMemo(() => {
    return members.filter(
      (member) =>
        (member.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (member.email || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        (member.phone || "").toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [members, searchQuery]);

  const fetchLinked = useCallback(async () => {
    // Add safety check
    if (!device || !device.id) {
      console.error("Device is not available");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const Response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/users/getLinkedUsers/${device.id}`
      );
      setMembers(Response.data || []);
    } catch (error) {
      console.error("Error fetching group details:", error);
      showNotification("Error fetching group details", "error");
    } finally {
      setLoading(false);
    }
  }, [device?.id]); // Memoize based on device.id only

  const showNotification = (message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  //optimized version
  const updateUserRecipients = async (member, field) => {
    setLoadingUpdate(true);
    let currentValues;
    if (field === "smsEnable") {
      currentValues = member.recipient
        ? member.recipient.smsEnable
        : member.members.map((m) => m.smsEnable);
    } else {
      currentValues = member.recipient
        ? member.recipient.emailEnable
        : member.members.map((m) => m.emailEnable);
    }

    let value = Array.isArray(currentValues) ? currentValues[0] : currentValues;
    value = !value; // Toggle the value

    const memberIds = member.members?.map((m) => m.id) || [];
    const deviceId = member.recipient?.deviceId;
    const userId = member.id;

    const idsArray = Array.isArray(memberIds) ? memberIds : [memberIds];
    const updateKey = buildUpdateKey(member, field);

    // Show spinner immediately
    setUpdatingUsers((prev) => {
      const newSet = new Set(prev);
      newSet.add(updateKey);
      return newSet;
    });

    try {
      // API call
      if (memberIds.length > 0) {
        await axios.put(
          `${process.env.REACT_APP_API_GATEAWAY}/members/updateMany`,
          {
            ids: idsArray,
            [field]: value,
            UI_Description: `${getUIDescription()}\n update one or many members`,
          }
        );
      } else if (userId && deviceId) {
        await axios.put(
          `${process.env.REACT_APP_API_GATEAWAY}/recipients?deviceId=${deviceId}&userId=${userId}`,
          { [field]: value, UI_Description: `${getUIDescription()}\n`, },
        );
      }

      // Update local state
      setMembers((prev) =>
        prev.map((user) => {
          // members to update
          const hasMemberToUpdate = user.members?.some((m) =>
            idsArray.includes(m.id)
          );

          // recipient to update (if user itself matches)
          const isDirectRecipient = user.recipient && user.id === member.id;

          if (hasMemberToUpdate || isDirectRecipient) {
            return {
              ...user,
              members: user.members?.map((m) =>
                idsArray.includes(m.id) ? { ...m, [field]: value } : m
              ),
              recipient: isDirectRecipient
                ? { ...user.recipient, [field]: value }
                : user.recipient,
            };
          }

          return user;
        })
      );

      showNotification(
        `${field === "smsEnable" ? "SMS" : "Email"} notification ${
          value ? "enabled" : "disabled"
        } successfully`,
        "success"
      );
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      showNotification(
        `Error updating ${
          field === "smsEnable" ? "SMS" : "Email"
        } notification`,
        "error"
      );
    } finally {
      // Hide spinner
      setUpdatingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(updateKey);
        return newSet;
      });
      setLoadingUpdate(false);
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsersForTable.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsersForTable.map((user) => user.id));
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const RecipientRow = React.memo(function RecipientRow({
    member,
    updatingUsers,
  }) {
    const smsKey = buildUpdateKey(member, "smsEnable");
    const emailKey = buildUpdateKey(member, "emailEnable");

    return (
      <TableRow
        key={member.id}
        sx={{
          backgroundColor: "#2f3136",
          "&:nth-of-type(odd)": { backgroundColor: "#3a3c43" },
          "&:hover": { backgroundColor: "#4a4d54" },
          "& td": {
            fontSize: { xs: "0.6rem", sm: "0.8rem", md: "0.9rem" },
            padding: { xs: "6px", sm: "8px", md: "16px" },
            wordBreak: "break-word",
            overflow: "hidden",
          },
        }}
      >
        <TableCell >
          <span title={member.name}>{member.name}</span>
        </TableCell>
        <TableCell>
          <span title={member.phone}>{member.phone}</span>
        </TableCell>
        <TableCell>
          <span title={member.email}>{member.email}</span>
        </TableCell>
        <TableCell sx={{ textAlign: "center", position: "relative" }}>
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <Checkbox
              checked={
                member.members?.[0]?.smsEnable ??
                member.recipient?.smsEnable ??
                false
              }
              disabled={updatingUsers.has(smsKey)}
              onChange={() => updateUserRecipients(member, "smsEnable")}
              size={window.innerWidth < 600 ? "small" : "medium"}
            />
            {updatingUsers.has(smsKey) && (
              <CircularProgress
                size={20}
                sx={{
                  color: "#60a5fa",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-10px",
                  marginLeft: "-10px",
                }}
              />
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ textAlign: "center", position: "relative" }}>
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <Checkbox
              checked={
                member.members?.[0]?.emailEnable ??
                member.recipient?.emailEnable ??
                false
              }
              onChange={() => updateUserRecipients(member, "emailEnable")}
              disabled={updatingUsers.has(emailKey)}
              size={window.innerWidth < 600 ? "small" : "medium"}
            />
            {updatingUsers.has(emailKey) && (
              <CircularProgress
                size={20}
                sx={{
                  color: "#60a5fa",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-10px",
                  marginLeft: "-10px",
                }}
              />
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  });

  const renderMainView = () => (
    <Box sx={{ height: "100%" }}>
      {/* Header */}

      {/* Manage recipient Component  */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" }, // Stack on mobile, side-by-side on desktop
          height: { xs: "auto", md: "75vh" },
          gap: 2,
        }}
      >
        {/* Left Panel - Recipients */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" }, // Full width on mobile, 50% on desktop
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            sx={{ p: { xs: 1,sm:3, md: 3 }, height: { xs: "auto", md: "20%" }, overflow: "hidden" }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              gutterBottom
              align="center"
              marginBottom={2}
            >
              {device?.name || "Device"} : alarms
            </Typography>
            <Box
              sx={{
                display: "inline",
                height: "100%",
                overflow: "auto",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" }, // Stack inputs on mobile
                  gap: 2,
                  width: "100%",
                }}
              >
                <TextField
                  label="Unit Name *"
                  value={device?.name || "No name"}
                  onChange={(e) => null}
                  fullWidth
                  sx={{ mb: 1 }}
                />

                <TextField
                  label="Modem Serial Number *"
                  value={device?.id || "No ID"}
                  onChange={(e) => null}
                  fullWidth
                />

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    height: "calc(100% - 60px)",
                    overflow: "hidden",
                  }}
                ></Box>
              </Box>
            </Box>
          </Paper>
          <Paper
            sx={{
              p: 2,
              height: { xs: "auto", md: "80%" },
              minHeight: { xs: "400px" }, // Minimum height for mobile
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Recipients Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Current recipients from this unit
              </Typography>
            </Box>

            {/* Search */}
            <TextField
              placeholder="Search By Name ..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
            />

            {/* Recipients Table */}
            <TableContainer
              sx={{
                overflowY: "auto",
                height: { xs: "400px", md: "63vh" },
                flex: 1,
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  width: "100%",
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: theme.palette.grey[100],
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        width: { xs: "25%", md: "16%" },
                        fontSize: {
                          xs: "0.7rem",
                          sm: "0.8rem",
                          md: "0.875rem",
                        },
                        padding: { xs: "6px", sm: "8px", md: "16px" },
                      }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        width: { xs: "25%", md: "14%" },
                        fontSize: {
                          xs: "0.7rem",
                          sm: "0.8rem",
                          md: "0.875rem",
                        },
                        padding: { xs: "6px", sm: "8px", md: "16px" },
                      }}
                    >
                      Phone
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        width: { xs: "30%", md: "40%" },
                        fontSize: {
                          xs: "0.7rem",
                          sm: "0.8rem",
                          md: "0.875rem",
                        },
                        padding: { xs: "6px", sm: "8px", md: "16px" },
                      }}
                    >
                      Email Address
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        width: { xs: "10%", md: "7%" },
                        fontSize: {
                          xs: "0.7rem",
                          sm: "0.8rem",
                          md: "0.875rem",
                        },
                        padding: { xs: "4px", sm: "6px", md: "16px" },
                        textAlign: "center",
                      }}
                    >
                      Text
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        width: { xs: "10%", md: "8%" },
                        fontSize: {
                          xs: "0.7rem",
                          sm: "0.8rem",
                          md: "0.875rem",
                        },
                        padding: { xs: "4px", sm: "6px", md: "16px" },
                        textAlign: "center",
                      }}
                    >
                      Email
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsersForTable.map((member) => (
                    <RecipientRow
                      key={member.id}
                      member={member}
                      updatingUsers={updatingUsers}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        {/* Right Panel */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" }, // Full width on mobile, 50% on desktop
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <Paper
            sx={{
              p: 2,
              pb: 1,
              height: { xs: "auto", md: "100%" },
              minHeight: { xs: "400px" }, // Minimum height for mobile
              overflow: "hidden",
            }}
          >
            <ManageRecipient
              device={device}
              onRecipientsUpdated={fetchLinked}
              members={members}
            />
          </Paper>
        </Box>
      </Box>
    </Box>
  );
  const renderTabContent = () => {
    switch (currentTab) {
      case "add-recipient":
        return renderMainView();

      case "remove-recipient":
        return (
          <DeleteRecipient
            members={members}
            device={device}
            onRecipientsUpdated={fetchLinked}
          />
        );

      case "notification-history":
        return <NotificationHistory device={device} />;

      case "priority-voice":
        return <PriorityVoice />;

      case "sms-delivery":
        return <SMSDelivery device={device} />;
      
      case "email-delivery":
        return <EmailDelivery device={device} />;

      default:
        return null;
    }
  };

  // Show loading or error message if device is not available
  // if (loading) {
  //   return (
  //     <Box
  //       sx={{
  //         display: "flex",
  //         justifyContent: "center",
  //         alignItems: "center",
  //         height: "100%",

  //       }}
  //     >
  //       <CircularProgress color="#60a5fa" size={100} />
  //     </Box>
  //   );
  // }

  if (!device) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Alert severity="error">
          Device information is not available. Please select a device.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%" }}>
      {/* Tab Content */}
      <Box sx={{ display: "flex", gap: 1, mb: 1, justifyContent: "flex-end" }}>
        <ButtonGroup
          sx={{
            justifyContent: "right",
            py: 0,
            gap: 0,
            bgcolor: "transparent",
            minHeight: "auto !important",
            display: "flex",
            flexDirection: {
              xs: "column", // Vertical on mobile
              sm: "row", // Horizontal on larger screens
            },
            width: {
              xs: "100%",
              sm: "auto",
            },
          }}
        >
          {loading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.6)",
                backdropFilter: "blur(4px)",
                borderRadius: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                zIndex: 5,
              }}
            >
              <CircularProgress color="#60a5fa" size={100} />
            </Box>
          )}
          <Button
            startIcon={<GroupIcon />}
            onClick={() => setCurrentTab("add-recipient")}
            sx={{
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: "none",
              fontSize: {
                xs: "12px",
                sm: "13px",
                md: "14px",
              },
              minWidth: {
                xs: "100%",
                sm: "50px",
                md: "90px",
                lg: "160px",
              },
              height: 48,
              bgcolor: currentTab === "add-recipient" ? "#1565c0" : "#374151",
              color: "white",
              "&:hover": {
                bgcolor: currentTab === "add-recipient" ? "#1565c0" : "#4b5563",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            Add recipient
          </Button>

          <Button
            startIcon={<DeleteForeverIcon />}
            onClick={() => setCurrentTab("remove-recipient")}
            sx={{
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: "none",
              fontSize: {
                xs: "12px",
                sm: "13px",
                md: "14px",
              },
              minWidth: {
                xs: "100%",
                sm: "150px",
                md: "90px",
                lg: "160px",
              },
              height: 48,
              bgcolor:
                currentTab === "remove-recipient" ? "#1565c0" : "#374151",
              color: "white",
              "&:hover": {
                bgcolor:
                  currentTab === "remove-recipient" ? "#1565c0" : "#4b5563",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            Remove Recipient
          </Button>

          <Button
            startIcon={<NotificationsNoneIcon />}
            onClick={() => setCurrentTab("notification-history")}
            sx={{
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: "none",
              fontSize: {
                xs: "12px",
                sm: "13px",
                md: "14px",
              },
              minWidth: {
                xs: "100%",
                sm: "130px",
                md: "90px",
                lg: "160px",
              },
              height: 48,
              bgcolor:
                currentTab === "notification-history" ? "#1565c0" : "#374151",
              color: "white",
              "&:hover": {
                bgcolor:
                  currentTab === "notification-history" ? "#1565c0" : "#4b5563",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            Notification History
          </Button>

          <Button
            startIcon={<CallIcon />}
            onClick={() => setCurrentTab("priority-voice")}
            sx={{
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: "none",
              fontSize: {
                xs: "12px",
                sm: "13px",
                md: "14px",
              },
              minWidth: {
                xs: "100%",
                sm: "130px",
                md: "90px",
                lg: "160px",
              },
              height: 48,
              bgcolor: currentTab === "priority-voice" ? "#1565c0" : "#374151",
              color: "white",
              "&:hover": {
                bgcolor:
                  currentTab === "priority-voice" ? "#1565c0" : "#4b5563",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            Priority Voice
          </Button>

          <Button
            startIcon={<SmsOutlined />}
            onClick={() => setCurrentTab("sms-delivery")}
            sx={{
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: "none",
              fontSize: {
                xs: "12px",
                sm: "13px",
                md: "14px",
              },
              minWidth: {
                xs: "100%",
                sm: "130px",
                md: "90px",
                lg: "160px",
              },
              height: 48,
              bgcolor: currentTab === "sms-delivery" ? "#1565c0" : "#374151",
              color: "white",
              "&:hover": {
                bgcolor: currentTab === "sms-delivery" ? "#1565c0" : "#4b5563",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            Sms Delivery
          </Button>
          <Button
            startIcon={<SmsOutlined />}
            onClick={() => setCurrentTab("email-delivery")}
            sx={{
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: "none",
              fontSize: {
                xs: "12px",
                sm: "13px",
                md: "14px",
              },
              minWidth: {
                xs: "100%",
                sm: "130px",
                md: "90px",
                lg: "160px",
              },
              height: 48,
              bgcolor: currentTab === "email-delivery" ? "#1565c0" : "#374151",
              color: "white",
              "&:hover": {
                bgcolor: currentTab === "email-delivery" ? "#1565c0" : "#4b5563",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            Email Delivery
          </Button>
        </ButtonGroup>
      </Box>
      <Box>{renderTabContent()}</Box>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
